import React from 'react';
import { TrendingUp, Target, Gauge, Package, Banknote } from 'lucide-react';
import { fmtXof, fmtPct } from './perfMeta';

const TONE = {
  green: 'bg-emerald-100 text-emerald-600',
  blue: 'bg-blue-100 text-blue-600',
  violet: 'bg-violet-100 text-violet-600',
  gray: 'bg-gray-100 text-gray-500',
};

// Anneau de progression CSS (conic-gradient)
export const ProgressRing = ({ pct, color = '#2563eb' }) => {
  const v = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="relative h-12 w-12 shrink-0 rounded-full" style={{ background: `conic-gradient(${color} ${v * 3.6}deg, #e5e7eb 0deg)` }}>
      <div className="absolute inset-[5px] flex items-center justify-center rounded-full bg-white text-[10px] font-bold text-gray-700">{Math.round(v)}%</div>
    </div>
  );
};

const Card = ({ icon: Icon, tone, label, value, sub, empty, ring, ringColor, testid }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5" data-testid={testid}>
    <div className="flex items-start justify-between gap-2">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${TONE[tone]}`}><Icon className="h-4.5 w-4.5" /></span>
      {ring !== undefined && <ProgressRing pct={ring} color={ringColor} />}
    </div>
    <p className="mt-3 text-xs font-medium text-gray-500">{label}</p>
    <p className={`mt-0.5 text-xl font-bold tracking-tight tabular-nums ${empty ? 'text-gray-400' : 'text-gray-900'}`}>{value}</p>
    <p className="mt-1 text-[11px] leading-snug text-gray-400">{sub}</p>
  </div>
);

// Section « Résultat du mois » — 5 KPI cards
export const PerfKpiCards = ({ totals }) => {
  const t = totals || {};
  const hasTarget = (t.caTarget || 0) > 0;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5" data-testid="perf-kpi-cards">
      <Card icon={TrendingUp} tone="green" label="CA réalisé" testid="perf-kpi-ca"
        value={fmtXof(t.ca)} empty={!t.ca}
        sub={t.ca ? 'Chiffre d\'affaires encaissé ce mois.' : 'Aucun CA enregistré sur cette période.'} />
      <Card icon={Target} tone="blue" label="Objectif mensuel" testid="perf-kpi-target"
        value={hasTarget ? fmtXof(t.caTarget) : 'Non défini'} empty={!hasTarget}
        sub={hasTarget ? 'Objectif d\'équipe pour la période.' : 'Définissez un objectif pour suivre la progression.'} />
      <Card icon={Gauge} tone="blue" label="Progression objectif" testid="perf-kpi-progress"
        value={fmtPct(t.progression)} empty={!hasTarget}
        ring={t.progression || 0} ringColor="#2563eb"
        sub={hasTarget ? 'Du CA réalisé sur l\'objectif.' : 'Objectif non défini.'} />
      <Card icon={Package} tone="violet" label="Opérations payées" testid="perf-kpi-ops"
        value={(t.ops || 0).toLocaleString('fr-FR')} empty={!t.ops}
        sub={t.ops ? 'Opérations encaissées ce mois.' : 'Aucune opération payée ce mois-ci.'} />
      <Card icon={Banknote} tone="green" label="Taux d'encaissement à 30j" testid="perf-kpi-recovery"
        value={fmtPct(t.cashRecovery?.pct)} empty={!t.cashRecovery?.paidWithin30d}
        ring={t.cashRecovery?.pct || 0} ringColor="#10b981"
        sub={`${fmtXof(t.cashRecovery?.paidWithin30d || 0)} encaissé sous 30 jours.`} />
    </div>
  );
};

export default PerfKpiCards;
